import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Paper, Grid,
  FormControlLabel, Switch, Alert, MenuItem,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

// Idiomas de plantilla de Meta. Vacío = el del servidor.
const TEMPLATE_LANGS = ['es', 'es_CL', 'es_PE', 'es_AR', 'es_MX', 'en_US'];

const pad = (n) => String(n).padStart(2, '0');
/** ISO → DD/MM/AAAA (convención de fechas del repo) */
const toDateLabel = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : '';
};

/**
 * Canal WhatsApp de la ORGANIZACIÓN (paso 4 del plan multi-organización):
 * qué número emisor le pertenece y con qué plantillas y límites opera.
 *
 * Sin número propio, la organización NO tiene WhatsApp salvo que un
 * super-admin le permita el número compartido de Grafton (switch
 * `sharedNumberAllowed`, 2026-10-26; el backend rechaza el cambio de otro
 * rol). El token de acceso no se expone ni se edita: es del portafolio de
 * Meta, no de la organización.
 */
const AtsWhatsappChannelTab = () => {
  const { t } = useTranslation();
  const tc = (k, o) => t(`ats.config.whatsappChannel.${k}`, o);
  const { organization, authToken, showAlert } = useStore.getState();
  const authConfig = {
    params: { organizationId: organization },
    headers: { Authorization: `Bearer ${authToken}` },
  };

  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState({ templates: {}, tier: {} });
  // Número compartido de Grafton (el del servidor) y si este usuario puede permitirlo
  const [sharedNumber, setSharedNumber] = useState({ publicNumber: null, available: false });
  const [canChangeShared, setCanChangeShared] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!organization) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get('/organizations/whatsapp-config', authConfig);
        const data = res.data.response?.data || {};
        setCfg(data.config || {});
        setDefaults(data.defaults || { templates: {}, tier: {} });
        setSharedNumber(data.sharedNumber || { publicNumber: null, available: false });
        setCanChangeShared(Boolean(data.canChangeSharedNumber));
      } catch (err) {
        console.error(err);
        showAlert(tc('loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (path, value) => setCfg((prev) => {
    const next = { ...prev };
    if (path.includes('.')) {
      const [a, b] = path.split('.');
      next[a] = { ...(next[a] || {}), [b]: value };
    } else next[path] = value;
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/organizations/whatsapp-config', cfg, authConfig);
      if (res.data.response?.status_response === 'OK') {
        setCfg(res.data.response.data?.config || cfg);
        showAlert(tc('saved'), 'success');
      } else {
        showAlert(res.data.response?.message || tc('saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      // 409 = el número ya está asignado a otra organización
      showAlert(err.response?.data?.response?.message || tc('saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }
  if (!cfg) return null;

  const field = (label, path, opts = {}) => (
    <Grid item xs={12} sm={opts.sm || 6}>
      <TextField
        label={label}
        value={(path.includes('.') ? (cfg[path.split('.')[0]] || {})[path.split('.')[1]] : cfg[path]) || ''}
        onChange={(e) => set(path, e.target.value)}
        placeholder={opts.placeholder || ''}
        helperText={opts.helperText || ''}
        type={opts.type || 'text'}
        fullWidth
        size="small"
      />
    </Grid>
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>{tc('title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{tc('description')}</Typography>

      {!cfg.phoneNumberId && (
        cfg.sharedNumberAllowed ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {tc('sharedOnNote', { number: sharedNumber.publicNumber ? `+${sharedNumber.publicNumber}` : '—' })}
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>{tc('noNumberNote')}</Alert>
        )
      )}

      {/* Número compartido de Grafton: solo un super-admin lo permite (2026-10-26) */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
        <FormControlLabel
          control={(
            <Switch
              checked={Boolean(cfg.sharedNumberAllowed)}
              disabled={!canChangeShared}
              onChange={(e) => set('sharedNumberAllowed', e.target.checked)}
            />
          )}
          label={tc('sharedNumberAllowed')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {tc('sharedNumberAllowedHint', { number: sharedNumber.publicNumber ? `+${sharedNumber.publicNumber}` : '—' })}
        </Typography>
        {!canChangeShared && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {tc('sharedNumberSuperAdminOnly')}
          </Typography>
        )}
        {cfg.sharedNumberAllowedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {tc(cfg.sharedNumberAllowed ? 'sharedNumberAllowedBy' : 'sharedNumberRemovedBy', {
              name: cfg.sharedNumberAllowedBy?.name || cfg.sharedNumberAllowedBy?.email || '—',
              date: toDateLabel(cfg.sharedNumberAllowedAt),
            })}
          </Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={Boolean(cfg.enabled)} onChange={(e) => set('enabled', e.target.checked)} />}
          label={tc('enabled')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {tc('enabledHint')}
        </Typography>
        <Grid container spacing={2}>
          {field(tc('phoneNumberId'), 'phoneNumberId', { helperText: tc('phoneNumberIdHint') })}
          {field(tc('publicNumber'), 'publicNumber', { helperText: tc('publicNumberHint') })}
          {field(tc('displayName'), 'displayName', { helperText: tc('displayNameHint') })}
          {field(tc('defaultPositionId'), 'defaultPositionId', { helperText: tc('defaultPositionIdHint') })}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{tc('templatesTitle')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>{tc('templatesHint')}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label={tc('templateLang')}
              value={(cfg.templates || {}).lang || ''}
              onChange={(e) => set('templates.lang', e.target.value)}
              helperText={tc('templateLangHint', { lang: defaults.templates?.lang || '—' })}
              fullWidth
              size="small"
            >
              <MenuItem value="">{tc('templateLangDefault', { lang: defaults.templates?.lang || '—' })}</MenuItem>
              {TEMPLATE_LANGS.map((l) => (<MenuItem key={l} value={l}>{l}</MenuItem>))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        disabled={saving}
        onClick={handleSave}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('save')}
      </Button>
    </Box>
  );
};

export default AtsWhatsappChannelTab;
